import type { ActivityType, Module } from "@bios/types";
import { MODULES, MODULES_BY_KEY, isModuleKey, topicsFor } from "./modules.js";

export {
  MODULES,
  MODULES_BY_KEY,
  MODULE_KEYS,
  isModuleKey,
  getModule,
  topicsFor,
  entitySubtypesFor,
} from "./modules.js";
export type { ModuleConfig, ModuleKey } from "./modules.js";

export {
  DATA_SOURCES_BY_MODULE,
  SEED_SOURCES_BY_MODULE,
  WORKFORCE_DATA_SOURCES,
  WORKFORCE_SIGNAL_SOURCES,
  dataSourcesFor,
  seedSourcesFor,
  workforce_data_sources,
  workforce_signal_sources,
} from "./seedSources.js";
export type { DataSourceSpec, SeedSourceSpec } from "./seedSources.js";

export interface ActivityDefinition {
  type: ActivityType;
  label: string;
  signalWeight: number;
  description: string;
}

export const ACTIVITIES: Record<ActivityType, ActivityDefinition> = {
  announcement: {
    type: "announcement",
    label: "Announcement",
    signalWeight: 0.55,
    description: "Verified statement from a primary actor.",
  },
  funding_round: {
    type: "funding_round",
    label: "Funding Round",
    signalWeight: 0.9,
    description: "Disclosed capital event with named participants.",
  },
  acquisition: {
    type: "acquisition",
    label: "Acquisition",
    signalWeight: 0.92,
    description: "Confirmed M&A activity.",
  },
  partnership: {
    type: "partnership",
    label: "Partnership",
    signalWeight: 0.7,
    description: "Joint commercial or strategic agreement.",
  },
  product_launch: {
    type: "product_launch",
    label: "Product Launch",
    signalWeight: 0.65,
    description: "New product or service shipping or available.",
  },
  executive_move: {
    type: "executive_move",
    label: "Executive Move",
    signalWeight: 0.6,
    description: "Leadership hire, exit, or board change.",
  },
  opinion: {
    type: "opinion",
    label: "Opinion",
    signalWeight: 0.15,
    description: "Editorial or commentary content.",
  },
  report: {
    type: "report",
    label: "Report",
    signalWeight: 0.75,
    description: "Research publication with named methodology.",
  },
  event: {
    type: "event",
    label: "Event",
    signalWeight: 0.45,
    description: "Conference, summit, or public event reference.",
  },
  rumor: {
    type: "rumor",
    label: "Rumor",
    signalWeight: 0.1,
    description: "Unverified claim or speculative content.",
  },
  marketing: {
    type: "marketing",
    label: "Marketing",
    signalWeight: 0.05,
    description: "Promotional or advertising content.",
  },
  // Phase 16: policy module activity types
  policy_proposal: {
    type: "policy_proposal",
    label: "Policy Proposal",
    signalWeight: 0.55,
    description: "Bill, draft, or proposal introduced but not enacted.",
  },
  policy_enacted: {
    type: "policy_enacted",
    label: "Policy Enacted",
    signalWeight: 0.9,
    description: "Legislation signed, regulation finalized, or rule in force.",
  },
  budget_approved: {
    type: "budget_approved",
    label: "Budget Approved",
    signalWeight: 0.85,
    description: "Appropriation or budget allocation approved with named amounts.",
  },
  executive_action: {
    type: "executive_action",
    label: "Executive Action",
    signalWeight: 0.85,
    description: "Executive order, directive, or unilateral agency action.",
  },
  regulatory_action: {
    type: "regulatory_action",
    label: "Regulatory Action",
    signalWeight: 0.8,
    description: "Regulatory body ruling, enforcement, or rulemaking step.",
  },
  grant_announced: {
    type: "grant_announced",
    label: "Grant Announced",
    signalWeight: 0.55,
    description: "Public grant program announced; award not yet disbursed.",
  },
  grant_disbursed: {
    type: "grant_disbursed",
    label: "Grant Disbursed",
    signalWeight: 0.85,
    description: "Confirmed disbursement to a named recipient.",
  },
  contract_awarded: {
    type: "contract_awarded",
    label: "Contract Awarded",
    signalWeight: 0.8,
    description: "Public-sector contract awarded to a named recipient.",
  },
  // Phase 34: legal module activity types
  lawsuit_filed: {
    type: "lawsuit_filed",
    label: "Lawsuit Filed",
    signalWeight: 0.68,
    description: "A lawsuit or legal complaint was filed.",
  },
  settlement: {
    type: "settlement",
    label: "Settlement",
    signalWeight: 0.82,
    description: "A legal dispute reached settlement terms.",
  },
  conviction: {
    type: "conviction",
    label: "Conviction",
    signalWeight: 0.86,
    description: "A criminal conviction or guilty finding occurred.",
  },
  acquittal: {
    type: "acquittal",
    label: "Acquittal",
    signalWeight: 0.82,
    description: "A defendant was acquitted or charges were rejected.",
  },
  policy_challenge: {
    type: "policy_challenge",
    label: "Policy Challenge",
    signalWeight: 0.72,
    description: "A law, rule, or policy was legally challenged.",
  },
  federal_investigation: {
    type: "federal_investigation",
    label: "Federal Investigation",
    signalWeight: 0.72,
    description: "A federal investigation or oversight action opened or advanced.",
  },
  enforcement_action: {
    type: "enforcement_action",
    label: "Enforcement Action",
    signalWeight: 0.88,
    description: "An agency or court took enforcement action.",
  },
  rights_expansion: {
    type: "rights_expansion",
    label: "Rights Expansion",
    signalWeight: 0.9,
    description: "A decision or action expanded rights or protections.",
  },
  rights_restriction: {
    type: "rights_restriction",
    label: "Rights Restriction",
    signalWeight: 0.9,
    description: "A decision or action restricted rights or protections.",
  },
  other: {
    type: "other",
    label: "Other",
    signalWeight: 0.3,
    description: "Uncategorized.",
  },
};

export const TOPICS_BY_MODULE: Record<string, readonly string[]> = Object.fromEntries(
  MODULES.map((m) => [m.key, m.topics]),
);

export const MARKETING_PHRASES = [
  "discount code",
  "limited time",
  "subscribe now",
  "buy now",
  "use code",
  "promo",
  "sponsored",
  "affiliate",
];

export const RUMOR_PHRASES = [
  "sources say",
  "rumored",
  "allegedly",
  "we hear",
  "speculation",
  "unconfirmed",
];

export function isModule(value: string): value is Module {
  return isModuleKey(value);
}
